// C:\Users\alan\Desktop\AGT\directiveai-react\agthealth\agthealth-frontend\src\components\pages\dashboard\widgets\AppointmentsWidget.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  useTheme,
  Tooltip,
  Alert,
  Paper
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { 
  Calendar, 
  Clock, 
  User, 
  MapPin, 
  Video, 
  Phone,
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar as CalendarIcon,
  ChevronRight
} from 'lucide-react';
import { format, isToday, isTomorrow, isThisWeek, parseISO, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { HEALTH_GREEN } from '../../../Login/utils/mockData';
import agthealthService from '../../../../services/agthealthService';
import { useNavigate } from 'react-router-dom';

// Componentes estilizados
const WidgetCard = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

const WidgetHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
}));

const WidgetContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0),
  flexGrow: 1,
  overflowY: 'auto',
  maxHeight: 380,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.text.primary, 0.1),
    borderRadius: '3px',
  },
}));

const WidgetFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
  display: 'flex',
  justifyContent: 'center'
}));

const AppointmentCard = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 2, 1.5),
  padding: theme.spacing(2),
  borderRadius: 16,
  backgroundColor: theme.palette.mode === 'dark' ? alpha('#FFFFFF', 0.05) : alpha('#F5F5F7', 0.7),
  position: 'relative'
}));

const AppointmentDate = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1.5),
}));

const AppointmentTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}));

const AppointmentDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.85rem',
}));

const PatientAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  fontWeight: 500,
  marginRight: theme.spacing(1.5),
  fontSize: '1rem'
}));

const StatusBadge = styled(Chip)(({ theme, status }) => {
  const statusColors = {
    scheduled: {
      bg: '#2196f3', // Azul sólido
      color: '#ffffff'
    },
    completed: {
      bg: HEALTH_GREEN, // Verde sólido
      color: '#ffffff'
    },
    cancelled: {
      bg: '#f44336', // Rojo sólido
      color: '#ffffff'
    },
    'no-show': {
      bg: '#ff9800', // Naranja sólido
      color: '#ffffff'
    }
  };
  
  const colorSet = statusColors[status] || statusColors.scheduled;
  
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    borderRadius: 8,
    fontSize: '0.75rem',
    height: 24,
    fontWeight: 500,
  };
});

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  fontWeight: 500,
}));

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: 6,
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.secondary, 0.1),
  }
}));

const AddIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  width: 32,
  height: 32
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: HEALTH_GREEN,
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: HEALTH_GREEN
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  }
}));

const NoAppointmentsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  height: '100%',
  minHeight: 260,
  textAlign: 'center'
}));

const AppointmentsWidget = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  
  // Estados para el diálogo de nueva cita
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [formData, setFormData] = useState({
    patientId: '',
    title: 'Consulta médica',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    duration: 30,
    type: 'in-person',
    status: 'scheduled',
    location: '',
    notes: '',
    reason: ''
  });
  
  // Estado para el diálogo de confirmación de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  
  // Cargar citas y pacientes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Obtener la fecha actual y la fecha límite (7 días después)
        const now = new Date();
        const startDate = now.toISOString();
        const endDate = addDays(now, 7).toISOString();
        
        // Obtener citas para los próximos 7 días
        const appointmentsResponse = await agthealthService.getAppointmentsByDateRange(startDate, endDate);
        
        // Obtener todos los pacientes
        const patientsResponse = await agthealthService.getPatients();
        
        setAppointments(appointmentsResponse.data || []);
        setPatients(patientsResponse.data || []);
        setError(null);
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError("No se pudieron cargar las citas");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Función para formatear la fecha de la cita
  const formatAppointmentDate = (dateString) => {
    try {
      // Usar la fecha local si está disponible, de lo contrario usar la fecha UTC
      const dateToUse = dateString;
      const date = parseISO(dateToUse);
  
      if (isToday(date)) {
        return `Hoy, ${format(date, 'HH:mm')}`;
      } else if (isTomorrow(date)) {
        return `Mañana, ${format(date, 'HH:mm')}`;
      } else if (isThisWeek(date)) {
        // Capitalizar primera letra del día
        const dayName = format(date, "EEEE", { locale: es });
        const capitalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
        return `${capitalizedDay}, ${format(date, 'HH:mm')}`;
      } else {
        return format(date, "dd MMM yyyy, HH:mm", { locale: es });
      }
    } catch (e) {
      console.error('Error al formatear fecha:', e);
      return 'Fecha inválida';
    }
  };
  
  // Función para obtener el nombre del paciente
  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.patientId === patientId);
    return patient 
      ? `${patient.firstName} ${patient.lastName}`
      : 'Paciente no encontrado';
  };

  // Función para generar iniciales del nombre del paciente
  const getPatientInitials = (patientId) => {
    const patient = patients.find(p => p.patientId === patientId);
    if (!patient) return '?';
    return `${patient.firstName?.charAt(0) || ''}${patient.lastName?.charAt(0) || ''}`;
  };
  
  // Función para obtener el texto según el estado de la cita
  const getStatusText = (status) => {
    switch (status) {
      case 'scheduled':
        return 'Programada';
      case 'completed':
        return 'Completada';
      case 'cancelled':
        return 'Cancelada';
      case 'no-show':
        return 'No asistió';
      default:
        return 'Desconocido';
    }
  };
  
  // Manejador para abrir el diálogo de nueva cita
  const handleNewAppointment = () => {
    setSelectedAppointment(null);
    setFormData({
      patientId: '',
      title: 'Consulta médica',
      date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      duration: 30,
      type: 'in-person',
      status: 'scheduled',
      location: '',
      notes: '',
      reason: ''
    });
    setDialogOpen(true);
  };
  
  // Manejador para enviar mensaje de WhatsApp
  const handleWhatsAppMessage = async () => {
    try {
      setWhatsappLoading(true);
      
      // Llamar al endpoint para enviar recordatorios de todas las citas próximas
      const response = await agthealthService.sendAllAppointmentReminders();
      
      // Mostrar notificación de éxito
      setSuccessMessage(`Se enviaron ${response.data.sent} recordatorios de ${response.data.total} citas`);
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error("Error al enviar recordatorios por WhatsApp:", error);
      setError("No se pudieron enviar los recordatorios por WhatsApp");
      
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setWhatsappLoading(false);
    }
  };
  
  // Manejador para editar una cita
  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      patientId: appointment.patientId,
      title: appointment.title || 'Consulta médica',
      date: appointment.date ? format(parseISO(appointment.date), "yyyy-MM-dd'T'HH:mm") : '',
      duration: appointment.duration || 30,
      type: appointment.type || 'in-person',
      status: appointment.status || 'scheduled',
      location: appointment.location || '',
      notes: appointment.notes || '',
      reason: appointment.reason || ''
    });
    setDialogOpen(true);
  };
  
  // Manejador para abrir el diálogo de confirmación de eliminación
  const handleDeleteConfirmation = (appointment) => {
    setAppointmentToDelete(appointment);
    setCancellationReason(''); // Resetear el motivo
    setDeleteDialogOpen(true);
  };
  
  // Manejador para eliminar una cita
  const handleDeleteAppointment = async () => {
    if (!appointmentToDelete) return;
    
    try {
      setLoading(true);
      
      // Primero, actualizar el estado a cancelado con el motivo
      await agthealthService.updateAppointment(appointmentToDelete.appointmentId, {
        status: 'cancelled',
        cancellationReason: cancellationReason || 'Solicitud del profesional'
      });
      
      // Luego eliminar la cita
      await agthealthService.deleteAppointment(appointmentToDelete.appointmentId);
      
      // Actualizar la lista de citas
      setAppointments(appointments.filter(a => a.appointmentId !== appointmentToDelete.appointmentId));
      
      setDeleteDialogOpen(false);
      setAppointmentToDelete(null);
      setCancellationReason('');
      setError(null);
      
      // Mostrar mensaje de éxito
      setSuccessMessage('Cita cancelada y notificación enviada al paciente');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error al eliminar cita:", error);
      setError("No se pudo eliminar la cita: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Manejador para cambiar los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Manejador para guardar una cita
  const handleSaveAppointment = async () => {
    try {
      setLoading(true);
      
      // Validar campos obligatorios
      if (!formData.patientId || !formData.date) {
        setError("Los campos de paciente y fecha son obligatorios");
        setLoading(false);
        return;
      }
      
      // Incluir la zona horaria local al enviar la fecha al backend
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const appointmentData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        timezone: timezone // Añadir la zona horaria local
      };
      
      let savedAppointment;
      
      if (selectedAppointment) {
        // Actualizar cita existente
        const response = await agthealthService.updateAppointment(
          selectedAppointment.appointmentId,
          appointmentData
        );
        savedAppointment = response.data;
        
        // Actualizar la lista de citas
        setAppointments(appointments.map(a => 
          a.appointmentId === selectedAppointment.appointmentId ? savedAppointment : a
        ));
        
        // Mostrar mensaje de éxito
        setSuccessMessage('Cita actualizada correctamente');
      } else {
        // Crear nueva cita
        const response = await agthealthService.createAppointment(appointmentData);
        savedAppointment = response.data;
        
        // Añadir la nueva cita a la lista
        setAppointments([...appointments, savedAppointment]);
        
        // Mostrar mensaje de éxito
        setSuccessMessage('Cita creada correctamente');
      }
      
      setDialogOpen(false);
      setError(null);
      
      // Ocultar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error al guardar cita:", err);
      setError(err.response?.data?.message || "No se pudo guardar la cita");
    } finally {
      setLoading(false);
    }
  };
  
  // Función para ver todas las citas
  const handleViewAllAppointments = () => {
    navigate('/appointments');
  };
  
  // Ordenar citas por fecha
  const sortedAppointments = [...appointments].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
  
  return (
    <WidgetCard>
      <WidgetHeader>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Calendar size={18} style={{ marginRight: 8 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Próximas Citas
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Botón de WhatsApp */}
          <Tooltip title="Enviar recordatorios por WhatsApp">
            <IconButton 
              sx={{ 
                color: '#25D366', // Color de WhatsApp
                mr: 1
              }}
              onClick={handleWhatsAppMessage}
              disabled={whatsappLoading}
            >
              {whatsappLoading ? (
                <CircularProgress size={20} sx={{ color: '#25D366' }} />
              ) : (
                /* Icono de WhatsApp */
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              )}
            </IconButton>
          </Tooltip>
          <AddIconButton onClick={handleNewAppointment} size="small">
            <Plus size={16} />
          </AddIconButton>
        </Box>
      </WidgetHeader>
      
      {/* Mensaje de éxito para envío de WhatsApp */}
      {successMessage && (
        <Alert severity="success" sx={{ m: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {/* Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}
      
      <WidgetContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress sx={{ color: HEALTH_GREEN }} />
          </Box>
        ) : sortedAppointments.length === 0 ? (
          <NoAppointmentsBox>
            <CalendarIcon size={40} strokeWidth={1.5} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ mb: 2 }}>
              No hay citas programadas para los próximos días
            </Typography>
            <ActionButton 
              startIcon={<Plus size={16} />}
              onClick={handleNewAppointment}
            >
              Programar Nueva Cita
            </ActionButton>
          </NoAppointmentsBox>
        ) : (
          <Box sx={{ py: 1.5 }}>
            {sortedAppointments.map((appointment) => (
              <AppointmentCard key={appointment.appointmentId}>
                {/* Fecha grande en negro alineada a la izquierda */}
                <AppointmentDate>
                  {formatAppointmentDate(appointment.date)}
                </AppointmentDate>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  {/* Avatar del paciente (reemplaza el ícono naranja) */}
                  <PatientAvatar>
                    {getPatientInitials(appointment.patientId)}
                  </PatientAvatar>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <AppointmentTitle>
                      {appointment.title || 'Consulta médica'}
                    </AppointmentTitle>
                    
                    <AppointmentDetail>
                      <User size={14} style={{ marginRight: 6, flexShrink: 0 }} />
                      <Typography variant="body2" noWrap>
                        {getPatientName(appointment.patientId)}
                      </Typography>
                    </AppointmentDetail>
                    
                    {appointment.location && (
                      <AppointmentDetail>
                        <MapPin size={14} style={{ marginRight: 6, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap>
                          {appointment.location}
                        </Typography>
                      </AppointmentDetail>
                    )}
                  </Box>
                  
                  <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <StatusBadge
                      size="small"
                      label={getStatusText(appointment.status)}
                      status={appointment.status}
                    />
                    <Box sx={{ mt: 1 }}>
                      <ActionIconButton 
                        size="small" 
                        onClick={() => handleEditAppointment(appointment)}
                        sx={{ mr: 0.5 }}
                      >
                        <Edit2 size={16} />
                      </ActionIconButton>
                      <ActionIconButton 
                        size="small" 
                        onClick={() => handleDeleteConfirmation(appointment)}
                      >
                        <Trash2 size={16} />
                      </ActionIconButton>
                    </Box>
                  </Box>
                </Box>
              </AppointmentCard>
            ))}
          </Box>
        )}
      </WidgetContent>
      
      <WidgetFooter>
        <Button 
          endIcon={<ChevronRight size={16} />}
          onClick={handleViewAllAppointments}
          sx={{ 
            textTransform: 'none',
            color: HEALTH_GREEN
          }}
        >
          Ver todas las citas
        </Button>
      </WidgetFooter>
      
      {/* Diálogo para crear/editar citas */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {selectedAppointment ? 'Editar Cita' : 'Nueva Cita'}
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="patient-label">Paciente *</InputLabel>
                <StyledSelect
                  labelId="patient-label"
                  name="patientId"
                  value={formData.patientId}
                  onChange={handleInputChange}
                  label="Paciente *"
                  disabled={loading}
                >
                  {patients.map(patient => (
                    <MenuItem key={patient.patientId} value={patient.patientId}>
                      {patient.firstName} {patient.lastName}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Título"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Fecha y Hora *"
                name="date"
                type="datetime-local"
                value={formData.date}
                onChange={handleInputChange}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="type-label">Tipo de Cita</InputLabel>
                <StyledSelect
                  labelId="type-label"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Tipo de Cita"
                  disabled={loading}
                >
                  <MenuItem value="in-person">Presencial</MenuItem>
                  <MenuItem value="video">Videollamada</MenuItem>
                  <MenuItem value="phone">Telefónica</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Duración (minutos)"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleInputChange}
                disabled={loading}
                InputProps={{ inputProps: { min: 5, step: 5 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Estado</InputLabel>
                <StyledSelect
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Estado"
                  disabled={loading}
                >
                  <MenuItem value="scheduled">Programada</MenuItem>
                  <MenuItem value="completed">Completada</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                  <MenuItem value="no-show">No asistió</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Ubicación"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={loading}
                placeholder={formData.type === 'in-person' ? 'Dirección o sala de consulta' : 'URL o número telefónico'}
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Motivo de la consulta"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                disabled={loading}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Notas adicionales"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={loading}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveAppointment}
            variant="contained"
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              bgcolor: HEALTH_GREEN,
              '&:hover': { bgcolor: '#3d8b40' }
            }}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1
          }
        }}
      >
        <DialogTitle>Confirmar cancelación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas cancelar esta cita? Esta acción no se puede deshacer.
          </Typography>
          
          <TextField
            fullWidth
            label="Motivo de la cancelación"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteAppointment}
            variant="contained"
            color="error"
            disabled={loading || !cancellationReason.trim()}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Eliminando...' : 'Cancelar cita'}
          </Button>
        </DialogActions>
      </Dialog>
    </WidgetCard>
  );
};

export default AppointmentsWidget;